import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import moment from "moment";
import {
  Calendar,
  MapPin,
  Tag,
  Clock,
  Banknote,
  Percent,
  Award,
  Truck,
  Users,
  Building2,
  Globe,
  Timer,
  AlertCircle,
  CheckCircle2,
  XCircle,
  X,
  Eye,
  Target,
  Megaphone,
  Copy,
  CheckCheck,
  Info,
  ScrollText,
  Ticket,
  RefreshCw,
  Store,
  CheckCircle,
  HourglassIcon,
} from "lucide-react";

const formatNumber = (num) => {
  if (!num || isNaN(num)) return '0';
  
  const absNum = Math.abs(Number(num));
  if (absNum >= 1000000) {
    return `${(absNum / 1000000).toFixed(1)}M`;
  } else if (absNum >= 1000) {
    return `${(absNum / 1000).toFixed(1)}K`;
  }
  return absNum.toString();
};

const calculateDiscount = (originalPrice, discountedPrice) => {
  // Convert inputs to numbers and handle invalid inputs
  const original = Number(originalPrice);
  const discounted = Number(discountedPrice);
  
  if (!original || !discounted || isNaN(original) || isNaN(discounted) || original <= 0) {
    return 0;
  }
  
  // Ensure discounted price isn't higher than original
  if (discounted > original) {
    return 0;
  }
  
  const discount = ((original - discounted) / original) * 100;
  return Math.round(discount);
};

const getStatusConfig = (status, type) => {
  if (!status) {
    return {
      icon: AlertCircle,
      text: 'Not Available',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-600',
      borderColor: 'border-gray-200'
    };
  }

  if (type === 'delivery') {
    switch (status.toLowerCase()) {
      case 'not_deployed':
        return {
          icon: Clock,
          text: 'Not Started',
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-600',
          borderColor: 'border-gray-200'
        };
      case 'partial':
        return {
          icon: HourglassIcon,
          text: 'Partially Delivered',
          bgColor: 'bg-amber-100',
          textColor: 'text-amber-600',
          borderColor: 'border-amber-200'
        };
      case 'completed':
        return {
          icon: CheckCircle2,
          text: 'Completed',
          bgColor: 'bg-green-100',
          textColor: 'text-green-600',
          borderColor: 'border-green-200'
        };
      default:
        return {
          icon: AlertCircle,
          text: 'Unknown Status',
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-600',
          borderColor: 'border-gray-200'
        };
    }
  } else {
    switch (status.toLowerCase()) {
      case 'pending':
        return {
          icon: Clock,
          text: 'Pending Approval',
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-600',
          borderColor: 'border-yellow-200'
        };
      case 'approved':
        return {
          icon: CheckCircle,
          text: 'Approved',
          bgColor: 'bg-green-100',
          textColor: 'text-green-600',
          borderColor: 'border-green-200'
        };
      case 'rejected':
        return {
          icon: XCircle,
          text: 'Rejected',
          bgColor: 'bg-red-100',
          textColor: 'text-red-600',
          borderColor: 'border-red-200'
        };
      default:
        return {
          icon: AlertCircle,
          text: 'Unknown Status',
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-600',
          borderColor: 'border-gray-200'
        };
    }
  }
};

const CampaignViewModal = ({ isOpen, onClose, campaign }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [copied, setCopied] = useState(false);

  // Return early if modal is not open or campaign is null
  if (!isOpen || !campaign) return null;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(campaign?.shorten_url || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Add null checks for campaign properties
  const audiencePercentage = campaign?.subscriber_count
    ? ((campaign?.initial_blast || 0) / campaign.subscriber_count) * 100 
    : 0;

    console.log("audiencePercentage", audiencePercentage)

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1000] bg-black/40 backdrop-blur-sm flex items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-[95%] max-w-5xl bg-white rounded-2xl shadow-2xl
                    max-h-[90vh] overflow-hidden mx-auto my-4"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b md:p-6">
            <div className="space-y-1">
              <h2 className="text-xl font-bold text-gray-900 md:text-2xl">
                Campaign Details
              </h2>
              <p className="text-xs text-gray-500 md:text-sm">
                View and manage campaign information
              </p>
            </div>
            {/* <div className="flex gap-2 items-center">
              { campaign.approval_status?.toLowerCase() === 'approved' && 
                campaign.delivery_status?.toLowerCase() !== 'completed' &&
                moment(campaign.end_date).isAfter(moment()) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Megaphone className="w-4 h-4" />
                  Blast Campaign
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 rounded-xl transition-colors hover:bg-gray-100"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div> */}
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 md:grid-cols-3 h-[calc(95vh-140px)]">
            {/* Left Sidebar - Campaign Image and Quick Stats */}
            <div className="overflow-y-auto border-b border-gray-200 md:border-b-0 md:border-r md:col-span-1">
              <div className="p-4 space-y-4 md:p-6 md:space-y-6">
                {/* Campaign Image */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="overflow-hidden relative rounded-xl aspect-video"
                >
                  <img
                    src={campaign.image}
                    alt={campaign.title}
                    className="object-cover w-full h-full"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t to-transparent from-black/60" />
                  <div className="absolute right-0 bottom-0 left-0 p-4">
                    <h3 className="text-lg font-semibold text-white">
                      {campaign.title}
                    </h3>
                  </div>
                </motion.div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="p-4 bg-purple-50 rounded-xl border border-purple-100"
                  >
                    <div className="flex gap-2 items-center mb-2">
                      <Target className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-purple-600">
                        Reach
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-purple-700">
                      {formatNumber(campaign?.subscriber_count)}
                    </p>
                    <p className="text-xs text-purple-600">Campaign reach</p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="p-4 bg-blue-50 rounded-xl border border-blue-100"
                  >
                    <div className="flex gap-2 items-center mb-2">
                      <Megaphone className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-600">
                        Audience
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-blue-700">
                      {`${parseFloat(audiencePercentage || 0).toFixed(2)}%`}
                    </p>
                    <p className="text-xs text-blue-600">Total Received</p>
                  </motion.div>
                </div>

                {/* Share Campaign - Hide on mobile, show on larger screens */}
                <div className="hidden space-y-3 md:block">
                  <h4 className="text-sm font-medium text-gray-700">
                    Share Campaign
                  </h4>
                  <div className="flex gap-2">
                    <div className="flex-1 px-3 py-2 text-sm text-gray-600 truncate bg-gray-50 rounded-lg">
                    {campaign?.shorten_url}
                    </div>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={handleCopyLink}
                      className={`px-3 py-2 rounded-lg flex items-center gap-1.5 ${
                        copied
                          ? "bg-green-500 text-white"
                          : "bg-gray-900 text-white hover:bg-gray-800"
                      } transition-colors duration-200`}
                    >
                      {copied ? (
                        <>
                          <CheckCheck className="w-4 h-4" />
                          <span className="text-sm">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span className="text-sm">Copy</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex overflow-hidden flex-col md:col-span-2">
              {/* Tabs */}
              <div className="flex overflow-x-auto p-2 space-x-1 border-b md:p-4 scrollbar-hide">
                {[
                  { id: "overview", label: "Overview", icon: Eye },
                  ...(campaign.price !== 0 && campaign.price !== null
                    ? [{ id: "pricing", label: "Pricing", icon: Banknote }]
                    : []),
                  { id: "locations", label: "Locations", icon: MapPin },
                  { id: "terms", label: "T&C", icon: ScrollText },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 text-xs md:text-sm 
                              font-medium rounded-lg whitespace-nowrap transition-colors ${
                                activeTab === tab.id
                                  ? "bg-gray-900 text-white"
                                  : "text-gray-600 hover:bg-gray-100"
                              }`}
                  >
                    <tab.icon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="overflow-y-auto flex-1 p-4 md:p-6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    {activeTab === "overview" && (
                      <div className="space-y-6">
                        {/* Campaign Status Section */}
                        <div className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200">
                          <h3 className="mb-4 text-lg font-semibold text-gray-900">Campaign Status</h3>
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            {/* Delivery Status */}
                            <div className="p-4 bg-white rounded-xl border border-gray-100">
                              <div className="flex justify-between items-center mb-3">
                                <span className="text-sm font-medium text-gray-600">Delivery Status</span>
                                <div className={`px-3 py-1 rounded-full text-xs font-medium 
                                  ${getStatusConfig(campaign.delivery_status, 'delivery').bgColor}
                                  ${getStatusConfig(campaign.delivery_status, 'delivery').textColor}`}
                                >
                                  <div className="flex items-center gap-1.5">
                                    {React.createElement(getStatusConfig(campaign.delivery_status, 'delivery').icon, {
                                      className: "w-3.5 h-3.5"
                                    })}
                                    <span>{getStatusConfig(campaign.delivery_status, 'delivery').text}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="overflow-hidden h-2 bg-gray-100 rounded-full">
                                <div 
                                  className={`h-full transition-all duration-500 ${
                                    campaign.delivery_status === 'completed' ? 'bg-green-500 w-full' :
                                    campaign.delivery_status === 'partial' ? 'bg-amber-500 w-1/2' :
                                    'bg-gray-300 w-0'
                                  }`}
                                />
                              </div>
                            </div>

                            {/* Approval Status */}
                            <div className="p-4 bg-white rounded-xl border border-gray-100">
                              <div className="flex justify-between items-center mb-3">
                                <span className="text-sm font-medium text-gray-600">Approval Status</span>
                                <div className={`px-3 py-1 rounded-full text-xs font-medium 
                                  ${getStatusConfig(campaign.approval_status, 'approval').bgColor}
                                  ${getStatusConfig(campaign.approval_status, 'approval').textColor}`}
                                >
                                  <div className="flex items-center gap-1.5">
                                    {React.createElement(getStatusConfig(campaign.approval_status, 'approval').icon, {
                                      className: "w-3.5 h-3.5"
                                    })}
                                    <span>{getStatusConfig(campaign.approval_status, 'approval').text}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="overflow-hidden h-2 bg-gray-100 rounded-full">
                                <div 
                                  className={`h-full transition-all duration-500 ${
                                    campaign.approval_status === 'Approved' ? 'bg-green-500 w-full' :
                                    campaign.approval_status === 'Rejected' ? 'bg-red-500 w-full' :
                                    'bg-yellow-500 w-1/2'
                                  }`}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200">
                          <h3 className="mb-4 text-lg font-semibold text-gray-900">
                            About Campaign
                          </h3>
                          <p className="leading-relaxed text-gray-600">
                            {campaign.description || "No description available"}
                          </p>
                        </div>

                        <div className="p-6 bg-gradient-to-br from-indigo-50 to-white rounded-xl border border-indigo-100">
                          <h3 className="mb-4 text-lg font-semibold text-gray-900">
                            Redemption Type
                          </h3>
                          <div className="flex flex-wrap gap-3">
                            {campaign.redemption_type?.map((type, index) => (
                              <div
                                key={index}
                                className="flex gap-2 items-center px-4 py-2 bg-white rounded-lg border border-indigo-200"
                              >
                                {type === "Online" ? (
                                  <>
                                    <Globe className="w-4 h-4 text-indigo-600" />
                                    <span className="text-sm font-medium text-indigo-600">
                                      Online Redemption
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <Store className="w-4 h-4 text-indigo-600" />
                                    <span className="text-sm font-medium text-indigo-600">
                                      In-Store Redemption
                                    </span>
                                  </>
                                )}
                              </div>
                            ))}
                            {(!campaign.redemption_type ||
                              campaign.redemption_type.length === 0) && (
                              <div className="flex gap-2 items-center px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
                                <AlertCircle className="w-4 h-4 text-gray-500" />
                                <span className="text-sm font-medium text-gray-500">
                                  No redemption type specified
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Categories */}
                        <div className="p-6 bg-gradient-to-br from-pink-50 to-white rounded-xl border border-pink-100">
                          <h3 className="mb-4 text-lg font-semibold text-gray-900">
                            Categories
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {campaign.category?.map((cat, index) => (
                              <span
                                key={index}
                                className="px-3 py-1.5 bg-white border border-pink-200 rounded-lg text-sm font-medium text-pink-600"
                              >
                                {cat}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === "pricing" &&
                      campaign.price !== 0 &&
                      campaign.price !== null && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="space-y-6"
                        >
                          {/* Main Pricing Card */}
                          <div className="p-6 bg-gradient-to-br from-emerald-50 to-white rounded-xl border border-emerald-100">
                            <div className="flex justify-between items-start mb-6">
                              <div className="space-y-1">
                                <h3 className="text-lg font-semibold text-gray-900">
                                  Pricing Details
                                </h3>
                                <p className="text-sm text-gray-500">
                                  Campaign pricing information
                                </p>
                              </div>
                              <div className="p-3 rounded-full bg-emerald-100/50">
                                <Banknote className="w-6 h-6 text-emerald-600" />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                              {/* Original Price */}
                              <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.1 }}
                                className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm"
                              >
                                <div className="flex gap-2 items-center mb-3">
                                  <Tag className="w-4 h-4 text-gray-600" />
                                  <span className="text-sm font-medium text-gray-600">
                                    Original Price
                                  </span>
                                </div>
                                <p className="text-2xl font-bold text-gray-900">
                                  ₵{parseFloat(campaign.price).toFixed(2)}
                                </p>
                              </motion.div>

                              {/* Discounted Price - Only show if exists and is not 0 */}
                              {campaign.discounted_price &&
                                campaign.discounted_price !== 0 &&
                                campaign.discounted_price !==
                                  campaign.price && (
                                  <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm"
                                  >
                                    <div className="flex gap-2 items-center mb-3">
                                      <Percent className="w-4 h-4 text-green-600" />
                                      <span className="text-sm font-medium text-green-600">
                                        Discounted Price
                                      </span>
                                    </div>
                                    <div className="space-y-1">
                                      <p className="text-2xl font-bold text-green-600">
                                        ₵
                                        {parseFloat(
                                          campaign.discounted_price
                                        ).toFixed(2)}
                                      </p>
                                      <p className="text-sm text-green-500">
                                        Save ₵
                                        {(
                                          campaign.price -
                                          campaign.discounted_price
                                        ).toFixed(2)}
                                      </p>
                                    </div>
                                  </motion.div>
                                )}

                              {/* Savings - Only show if there's a valid discount */}
                              {campaign.discounted_price &&
                                campaign.discounted_price !== 0 &&
                                campaign.discounted_price !==
                                  campaign.price && (
                                  <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className="p-4 bg-gradient-to-br from-pink-50 to-white rounded-xl border border-pink-100"
                                  >
                                    <div className="flex gap-2 items-center mb-3">
                                      <Award className="w-4 h-4 text-pink-600" />
                                      <span className="text-sm font-medium text-pink-600">
                                        Total Savings
                                      </span>
                                    </div>
                                    <div className="space-y-1">
                                      <p className="text-2xl font-bold text-pink-600">
                                        {calculateDiscount(
                                          campaign.price,
                                          campaign.discounted_price
                                        )}
                                        %
                                      </p>
                                      <p className="text-sm text-pink-500">
                                        Save ₵{(
                                          Number(campaign.price) - 
                                          Number(campaign.discounted_price)
                                        ).toFixed(2)}
                                      </p>
                                    </div>
                                  </motion.div>
                                )}
                            </div>
                          </div>

                          {/* Price Validity */}
                          <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="p-6 bg-purple-50 rounded-xl border border-purple-100"
                          >
                            <div className="flex gap-3 items-center mb-4">
                              <div className="p-2 bg-purple-100 rounded-lg">
                                <Timer className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <h4 className="font-medium text-purple-900">
                                  Price Validity
                                </h4>
                                <p className="text-sm text-purple-600">
                                  Limited time pricing
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2 items-center text-sm text-purple-700">
                              <Calendar className="w-4 h-4" />
                              <span>
                                Valid until{" "}
                                {moment(campaign.end_date).format(
                                  "DD MMM, YYYY"
                                )}
                              </span>
                            </div>
                          </motion.div>

                          {/* Price Notice */}
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                            className="flex gap-2 items-center p-4 bg-amber-50 rounded-lg border border-amber-100"
                          >
                            <AlertCircle className="flex-shrink-0 w-5 h-5 text-amber-600" />
                            <p className="text-sm text-amber-700">
                              Prices are subject to change. All prices are
                              inclusive of applicable taxes.
                            </p>
                          </motion.div>
                        </motion.div>
                      )}

                    {activeTab === "locations" && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                      >
                        {/* Location Overview Card */}
                        <div className="p-6 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100">
                          <div className="flex justify-between items-start mb-6">
                            <div className="space-y-1">
                              <h3 className="text-lg font-semibold text-gray-900">
                                Campaign Locations
                              </h3>
                              <p className="text-sm text-gray-500">
                                Areas where this campaign is active
                              </p>
                            </div>
                            <div className="p-3 rounded-full bg-blue-100/50">
                              <MapPin className="w-6 h-6 text-blue-600" />
                            </div>
                          </div>

                          <div className="space-y-4">
                            {campaign.location?.length > 0 ? (
                              campaign.location.map((loc, index) => (
                                <motion.div
                                  key={index}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: index * 0.1 }}
                                  className="p-4 bg-white rounded-xl border border-blue-100 shadow-sm"
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="space-y-2">
                                      <div className="flex gap-2 items-center">
                                        <Building2 className="w-4 h-4 text-blue-600" />
                                        <span className="font-medium text-gray-900">
                                          {loc.location === "all"
                                            ? "All Locations"
                                            : loc.location}
                                        </span>
                                      </div>
                                      {loc.location !== "all" && (
                                        <div className="flex gap-2 items-center">
                                          <Globe className="w-4 h-4 text-gray-500" />
                                          <span className="text-sm text-gray-600">
                                            {loc.region}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <div
                                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                                        loc.location === "all"
                                          ? "bg-purple-100 text-purple-600"
                                          : "bg-blue-100 text-blue-600"
                                      }`}
                                    >
                                      {loc.location === "all"
                                        ? "Nationwide"
                                        : "Regional"}
                                    </div>
                                  </div>
                                </motion.div>
                              ))
                            ) : (
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="flex justify-center items-center p-8 bg-gray-50 rounded-xl border border-gray-200 border-dashed"
                              >
                                <div className="text-center">
                                  <MapPin className="mx-auto mb-2 w-8 h-8 text-gray-400" />
                                  <p className="text-gray-500">
                                    No locations specified
                                  </p>
                                </div>
                              </motion.div>
                            )}
                          </div>
                        </div>

                        {/* Coverage Information */}
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                          className="grid grid-cols-1 gap-4 md:grid-cols-2"
                        >
                          {/* Coverage Stats */}
                          <div className="p-5 bg-gradient-to-br from-indigo-50 to-white rounded-xl border border-indigo-100">
                            <div className="flex gap-3 items-center mb-4">
                              <div className="p-2 bg-indigo-100 rounded-lg">
                                <Users className="w-5 h-5 text-indigo-600" />
                              </div>
                              <div>
                                <h4 className="font-medium text-indigo-900">
                                  Coverage
                                </h4>
                                <p className="text-sm text-indigo-600">
                                  Potential reach in selected areas
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-4 items-center">
                              <div className="overflow-hidden flex-1 h-2 bg-indigo-100 rounded-full">
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{ width: "75%" }}
                                  transition={{ delay: 0.5, duration: 1 }}
                                  className="h-full bg-indigo-500"
                                />
                              </div>
                              <span className="text-sm font-medium text-indigo-600">
                                75%
                              </span>
                            </div>
                          </div>

                          {/* Location Notice */}
                          <div className="p-5 bg-gradient-to-br from-amber-50 to-white rounded-xl border border-amber-100">
                            <div className="flex gap-3 items-center">
                              <div className="p-2 bg-amber-100 rounded-lg">
                                <AlertCircle className="w-5 h-5 text-amber-600" />
                              </div>
                              <div>
                                <h4 className="font-medium text-amber-900">
                                  Location Notice
                                </h4>
                                <p className="text-sm text-amber-600">
                                  Campaign visibility is limited to selected
                                  regions
                                </p>
                              </div>
                            </div>
                          </div>
                        </motion.div>

                        {/* Additional Information */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                          className="flex gap-2 items-center p-4 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <Info className="flex-shrink-0 w-5 h-5 text-gray-600" />
                          <p className="text-sm text-gray-600">
                            Location availability may vary based on regional
                            restrictions and campaign settings.
                          </p>
                        </motion.div>
                      </motion.div>
                    )}

                    {activeTab === "terms" && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                      >
                        {/* Redemption Instructions */}
                        <div className="p-6 bg-gradient-to-br from-emerald-50 to-white rounded-xl border border-emerald-100">
                          <div className="flex justify-between items-start mb-4">
                            <div className="space-y-1">
                              <h3 className="text-lg font-semibold text-gray-900">
                                Redemption Instructions
                              </h3>
                              <p className="text-sm text-gray-500">
                                How to redeem this campaign offer
                              </p>
                            </div>
                            <div className="p-2 bg-emerald-100 rounded-lg">
                              <Ticket className="w-5 h-5 text-emerald-600" />
                            </div>
                          </div>
                          <div className="space-y-3">
                            <motion.div
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.1 }}
                              className="flex gap-3 items-start p-3 bg-white rounded-lg border border-emerald-100"
                            >
                              <div className="flex justify-center items-center w-6 h-6 text-sm font-medium text-emerald-600 bg-emerald-100 rounded-full">
                                1
                              </div>
                              <p className="text-sm text-gray-600">
                                {campaign.redemption_instructions ||
                                  "Present the campaign code at the time of purchase"}
                              </p>
                            </motion.div>
                          </div>
                        </div>

                        {/* Refund Policy */}
                        <div className="p-6 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100">
                          <div className="flex justify-between items-start mb-4">
                            <div className="space-y-1">
                              <h3 className="text-lg font-semibold text-gray-900">
                                Refund Policy
                              </h3>
                              <p className="text-sm text-gray-500">
                                Our refund and cancellation terms
                              </p>
                            </div>
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <RefreshCw className="w-5 h-5 text-blue-600" />
                            </div>
                          </div>
                          <div className="p-4 bg-white rounded-lg border border-blue-100">
                            <p className="text-sm leading-relaxed text-gray-600">
                              {campaign.refund_policy ||
                                "All sales are final. No refunds will be issued unless required by law. " +
                                  "Please contact customer support for any concerns regarding your purchase."}
                            </p>
                          </div>
                        </div>

                        {/* Terms and Conditions */}
                        <div className="p-6 bg-gradient-to-br from-purple-50 to-white rounded-xl border border-purple-100">
                          <div className="flex justify-between items-start mb-4">
                            <div className="space-y-1">
                              <h3 className="text-lg font-semibold text-gray-900">
                                Terms & Conditions
                              </h3>
                              <p className="text-sm text-gray-500">
                                Campaign terms of use
                              </p>
                            </div>
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <ScrollText className="w-5 h-5 text-purple-600" />
                            </div>
                          </div>
                          <div className="space-y-4">
                            <div className="p-4 bg-white rounded-lg border border-purple-100">
                              <p className="text-sm leading-relaxed text-gray-600">
                                {campaign.terms_and_conditions ||
                                  "By participating in this campaign, you agree to the following terms and conditions. " +
                                    "The offer is subject to availability and may be withdrawn at any time. " +
                                    "Cannot be combined with other offers or promotions."}
                              </p>
                            </div>

                            {/* Additional Info Notice */}
                            <motion.div
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.4 }}
                              className="flex gap-2 items-center p-3 rounded-lg border border-blue-100 bg-blue-50/50"
                            >
                              <AlertCircle className="w-4 h-4 text-blue-600" />
                              <span className="text-sm text-blue-600">
                                These terms are subject to change. Last updated{" "}
                                {moment().format("MMMM DD, YYYY")}
                              </span>
                            </motion.div>
                          </div>
                        </div>

                        {/* Footer Notice */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5 }}
                          className="flex gap-2 justify-center items-center mt-6 text-sm text-gray-500"
                        >
                          <Info className="w-4 h-4" />
                          <span>
                            Please read all terms carefully before redeeming the
                            offer
                          </span>
                        </motion.div>
                      </motion.div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CampaignViewModal;
